// EventRegistrationPage_MercadoPago_final_fix_v2.tsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Paper, 
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Divider,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useParams, useLocation, Link } from 'react-router-dom';
import { MetaConsentBanner, MetaConsentManageLink, readMetaBrowserContext, useEventMetaPixel } from '../../features/meta-pixel';
import { useForm, Controller, FormProvider, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { phoneValidation, applyPhoneMask } from '../../utils/phoneValidation';
import { 
  applyCpfMask, 
  applyCnpjMask, 
  applyCepMask, 
  applyIdadeMask, 
  applyDataNascimentoMask,
  validateCPF,
  validateCNPJ,
  validateCEP,
  validateIdade,
  validateDataNascimento
} from '../../utils/fieldMasks';
import { eventService } from '../../services/eventService';
import { replaceWithExclusiveDomain } from '../../utils/exclusiveDomainRedirect';
import { registrationService } from '../../services/registrationService';
import paymentServiceMercadoPago from '../../services/mercadoPagoService';
import CheckoutPaymentRouter, { type CheckoutTab } from './components/CheckoutPaymentRouter';
import PixCopyCodeField from './components/PixCopyCodeField';
import type { PaymentProviderRoutesDto } from '../../types/checkoutPayment';
import LegalModal from '../../components/LegalModal';
import { legalService, purchaseAcceptedFromTemplates, type LegalAcceptedItem } from '../../services/legalService';
import EmailIcon from '@mui/icons-material/Email';
import { formatUnknownError } from '../../utils/formatUnknownError';
import ActiveLotBanner from '../../components/events/ActiveLotBanner';
import { getTicketLotName } from '../../components/events/lotDisplayUtils';
import { calculateTicketTotalsWithFee, roundMoney } from '../../utils/money';
import type { ActiveSalesPhaseInfo } from '../../components/events/lotDisplayUtils';
import { customFieldAppliesToTicket, normalizeCustomFieldScope } from '../../utils/customFieldTicketScope';
import { getSafeTicketDescription } from '../../utils/ticketDisplay';
import {
  getRegistrationQuantityLimits,
  hasExplicitMultiPurchase,
} from '../../utils/ticketCart';
import EventCartCheckout from './EventCartCheckout';
import RegistrationSuccessView from './components/RegistrationSuccessView';
import { useBrandRegistrationCopy } from '../../brands/registrationCopy';
import { CHECKOUT_PUBLIC_ERROR_MESSAGE } from '../../utils/checkoutPublicError';

const toErrorMessage = (_error: unknown): string => CHECKOUT_PUBLIC_ERROR_MESSAGE;

const getPixFailureMessage = (_data?: any): string => CHECKOUT_PUBLIC_ERROR_MESSAGE;

// Função para sugerir correções de domínios comuns
const suggestEmailDomain = (email: string): string | null => {
  const commonDomains = [
    'gmail.com', 'hotmail.com', 'outlook.com', 'yahoo.com', 'live.com',
    'icloud.com', 'uol.com.br', 'terra.com.br', 'bol.com.br', 'ig.com.br'
  ];
  
  const typos: Record<string, string> = {
    'gmial.com': 'gmail.com',
    'gmai.com': 'gmail.com',
    'gmil.com': 'gmail.com',
    'gmaul.com': 'gmail.com',
    'gamil.com': 'gmail.com',
    'hotmial.com': 'hotmail.com',
    'hotmai.com': 'hotmail.com',
    'hotmil.com': 'hotmail.com',
    'outlok.com': 'outlook.com',
    'outloo.com': 'outlook.com',
    'yahooo.com': 'yahoo.com',
    'yaho.com': 'yahoo.com',
    'yhoo.com': 'yahoo.com',
  };

  const parts = email.split('@');
  if (parts.length !== 2) return null;
  
  const domain = parts[1].toLowerCase();
  
  // Verificar erros de digitação conhecidos
  if (typos[domain]) {
    return `${parts[0]}@${typos[domain]}`;
  }
  
  // Verificar similaridade com domínios comuns
  for (const commonDomain of commonDomains) {
    if (domain.includes(commonDomain.substring(0, 4)) && domain !== commonDomain) {
      return `${parts[0]}@${commonDomain}`;
    }
  }
  
  return null;
};

// Interface for the form data
interface RegistrationFormData {
  name: string;
  email: string;
  emailConfirmation: string;
  phone: string;
  ticketId: string;
  quantity: number;
  customFields: Record<string, string | boolean | string[]>;
  agreeTerms: boolean;
  cpf?: string;
  cardHolderName?: string;
  cardNumber?: string;
  cardExpirationDate?: string;
  cardExpirationMonth?: string; // Explicitly needed for type matching
  cardExpirationYear?: string;  // Explicitly needed for type matching
  cardCvv?: string;
  installments?: string;
  cardHolderCPF?: string;
  issuerId?: string;
}

// Interfaces for EventData, TicketData, CustomFieldData, EventResponse
interface EventData {
  _id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  location: string;
  address: string;
  city: string;
  state: string;
  coverImage?: string;
  galleryImages?: string[];
  isPaid: boolean;
  capacity: number;
  category: string;
  parishName?: string;
  tickets: TicketData[];
  customFields?: CustomFieldData[];
  allowGroupRegistration?: boolean; // Added this field
  feePercentage?: number; // Taxa da paróquia em percentual
  allowInstallments?: boolean;
  postRegistrationUrl?: string; // Link opcional exibido após a inscrição
  ticketCatalogMode?: 'legacy' | 'batched' | 'phased';
  activeSalesPhase?: ActiveSalesPhaseInfo | null;
  metaPixel?: {
    pixelId?: string;
    enabled?: boolean;
  };
  requiresBuyerCpf?: boolean;
  status?: string;
}
interface TicketData {
  _id: string;
  name: string;
  description: string;
  price: number;
  quantity: number;
  startSaleDate?: string;
  endSaleDate?: string;
  salesPhaseName?: string;
  batchName?: string;
  ticketTypeId?: string;
  allowInstallments?: boolean;
  showPlatformFee?: boolean; // Mostrar taxa da plataforma para este ingresso específico
  isRestricted?: boolean; // Se true, apenas emails na lista podem comprar
  allowedEmails?: string[]; // Lista de emails permitidos
  minPerOrder?: number;
  maxPerOrder?: number | null;
  allowSameAttendeeData?: boolean;
}
interface CustomFieldData {
  _id: string;
  fieldName: string;
  fieldType: 'text' | 'select' | 'checkbox' | 'number' | 'label' | 'cpf' | 'cnpj' | 'idade' | 'cep' | 'dataNascimento';
  isRequired: boolean;
  options?: string[];
  eventId?: string;
  order?: number;
  appliesToAllTickets?: boolean;
  ticketScopeIds?: string[];
}

// --- Define Validation Schema --- 
// Ensure ALL fields from RegistrationFormData are present, even if just optional
// Função para criar schema de validação dinâmico
const createValidationSchema = (customFields: CustomFieldData[]) => {
  const customFieldsSchema: { [key: string]: any } = {};
  
  customFields.forEach(field => {
    // Usar _id como chave única para evitar conflitos com campos de mesmo nome
    const fieldKey = field._id;
    
    if (field.isRequired) {
      switch (field.fieldType) {
        case 'text':
        case 'number':
          customFieldsSchema[fieldKey] = yup.string().required(`${field.fieldName} é obrigatório`);
          break;
        case 'cpf':
          customFieldsSchema[fieldKey] = yup
            .string()
            .required(`${field.fieldName} é obrigatório`)
            .test('cpf-valid', 'CPF inválido', (value) => !value || validateCPF(value));
          break;
        case 'cnpj':
          customFieldsSchema[fieldKey] = yup
            .string()
            .required(`${field.fieldName} é obrigatório`)
            .test('cnpj-valid', 'CNPJ inválido', (value) => !value || validateCNPJ(value));
          break;
        case 'cep':
          customFieldsSchema[fieldKey] = yup
            .string()
            .required(`${field.fieldName} é obrigatório`)
            .test('cep-valid', 'CEP inválido', (value) => !value || validateCEP(value));
          break;
        case 'idade':
          customFieldsSchema[fieldKey] = yup
            .string()
            .required(`${field.fieldName} é obrigatório`)
            .test('idade-valid', 'Idade inválida (deve ser entre 0 e 150)', (value) => !value || validateIdade(value));
          break;
        case 'dataNascimento':
          customFieldsSchema[fieldKey] = yup
            .string()
            .required(`${field.fieldName} é obrigatório`)
            .test('data-valid', 'Data de nascimento inválida', (value) => !value || validateDataNascimento(value));
          break;
        case 'select':
          customFieldsSchema[fieldKey] = yup.string().required(`${field.fieldName} é obrigatório`).nullable();
          break;
        case 'checkbox':
          customFieldsSchema[fieldKey] = yup.array().min(1, `${field.fieldName} é obrigatório`).default([]);
          break;
        default:
          customFieldsSchema[fieldKey] = yup.string();
      }
    } else {
      if (field.fieldType === 'checkbox') {
        customFieldsSchema[fieldKey] = yup.array().optional().default([]);
      } else if (field.fieldType === 'cpf') {
        customFieldsSchema[fieldKey] = yup
          .string()
          .optional()
          .test('cpf-valid', 'CPF inválido', (value) => !value || validateCPF(value));
      } else if (field.fieldType === 'cnpj') {
        customFieldsSchema[fieldKey] = yup
          .string()
          .optional()
          .test('cnpj-valid', 'CNPJ inválido', (value) => !value || validateCNPJ(value));
      } else if (field.fieldType === 'cep') {
        customFieldsSchema[fieldKey] = yup
          .string()
          .optional()
          .test('cep-valid', 'CEP inválido', (value) => !value || validateCEP(value));
      } else if (field.fieldType === 'idade') {
        customFieldsSchema[fieldKey] = yup
          .string()
          .optional()
          .test('idade-valid', 'Idade inválida (deve ser entre 0 e 150)', (value) => !value || validateIdade(value));
      } else if (field.fieldType === 'dataNascimento') {
        customFieldsSchema[fieldKey] = yup
          .string()
          .optional()
          .test('data-valid', 'Data de nascimento inválida', (value) => !value || validateDataNascimento(value));
      } else {
        customFieldsSchema[fieldKey] = yup.string().optional().default('');
      }
    }
  });

  return yup.object().shape({
    name: yup
      .string()
      .required('Nome é obrigatório')
      .min(2, 'Nome deve ter pelo menos 2 caracteres')
      .max(100, 'Nome deve ter no máximo 100 caracteres')
      .matches(
        /^[a-zA-ZÀ-ÿ\s'-]+$/,
        'Nome deve conter apenas letras, espaços, hífens e apóstrofos'
      )
      .test('no-numbers', 'Nome não pode conter números', (value) => {
        if (!value) return true;
        return !/\d/.test(value);
      })
      .test('min-words', 'Nome deve conter pelo menos um nome completo', (value) => {
        if (!value) return true;
        const words = value.trim().split(/\s+/).filter(word => word.length > 0);
        return words.length >= 1 && words.every(word => /^[a-zA-ZÀ-ÿ'-]+$/.test(word));
      }),
    email: yup.string().email('Email inválido').required('Email é obrigatório'),
    emailConfirmation: yup.string()
      .email('Email inválido')
      .required('Confirmação de email é obrigatória')
      .oneOf([yup.ref('email')], 'Os emails não coincidem'),
    phone: yup
      .string()
      .required('Telefone é obrigatório')
      .test('phone-validation', 'Telefone inválido', phoneValidation('Telefone inválido')),
    ticketId: yup.string().required('Selecione um ingresso'),
    quantity: yup.number().min(1, 'Quantidade mínima é 1').required('Quantidade é obrigatória'),
    customFields: yup.object().shape(customFieldsSchema),
    agreeTerms: yup.boolean().oneOf([true], 'Você deve concordar com os termos'),
    cpf: yup.string().optional(),

    // Payment fields - conditionally required
  cardHolderName: yup.string().when(['$isPaidTicket', '$isFinalStep'], {
    is: (isPaidTicket: boolean, isFinalStep: boolean) => isPaidTicket && isFinalStep,
    then: (schema) => schema.required('Nome do titular é obrigatório'),
    otherwise: (schema) => schema.optional(),
  }),
  cardNumber: yup.string().when(['$isPaidTicket', '$isFinalStep'], {
    is: (isPaidTicket: boolean, isFinalStep: boolean) => isPaidTicket && isFinalStep,
    then: (schema) => schema.required('Número do cartão é obrigatório'),
    otherwise: (schema) => schema.optional(),
  }),
  cardExpirationDate: yup.string().when(['$isPaidTicket', '$isFinalStep'], {
    is: (isPaidTicket: boolean, isFinalStep: boolean) => isPaidTicket && isFinalStep,
    then: (schema) => schema
      .required('Validade é obrigatória')
      .matches(/^(0[1-9]|1[0-2])\/?([0-9]{4}|[0-9]{2})$/, 'Formato MM/AA ou MM/AAAA')
      .test('is-expired', 'Cartão expirado', (value) => {
          if (!value) return false;
          const [month, year] = value.split('/');
          if (!month || !year) return false;
          const fullYear = year.length === 2 ? parseInt(`20${year}`) : parseInt(year);
          const expiryDate = new Date(fullYear, parseInt(month), 0); // Last day of expiration month
          const currentDate = new Date();
          currentDate.setDate(1); 
          currentDate.setHours(0, 0, 0, 0);
          return expiryDate >= currentDate;
      }),
    otherwise: (schema) => schema.optional(),
  }),
  // Add optional fields explicitly for type matching
  cardExpirationMonth: yup.string().optional(), 
  cardExpirationYear: yup.string().optional(),
  issuerId: yup.string().optional(),
  
  cardCvv: yup.string().when(['$isPaidTicket', '$isFinalStep'], {
    is: (isPaidTicket: boolean, isFinalStep: boolean) => isPaidTicket && isFinalStep,
    then: (schema) => schema.required('CVV é obrigatório').matches(/^[0-9]{3,4}$/, 'CVV inválido'),
    otherwise: (schema) => schema.optional(),
  }),
  installments: yup.string().when(['$isPaidTicket', '$isFinalStep', '$allowInstallments'], {
    is: (isPaidTicket: boolean, isFinalStep: boolean, allowInstallments: boolean) => 
      isPaidTicket && isFinalStep && allowInstallments === true,
    then: (schema) => schema.required('Selecione o número de parcelas'),
    otherwise: (schema) => schema.optional(),
  }),
  cardHolderCPF: yup.string().when(['$isPaidTicket', '$isFinalStep'], {
    is: (isPaidTicket: boolean, isFinalStep: boolean) => isPaidTicket && isFinalStep,
    then: (schema) => schema.required('CPF do titular é obrigatório').matches(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, 'CPF inválido'),
    otherwise: (schema) => schema.optional(),
    }),
  });
};

// Component for the event registration page
const EventRegistrationPage: React.FC = () => {
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const t = useBrandRegistrationCopy();
  
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const queryParams = new URLSearchParams(location.search);
  const preSelectedTicketId = queryParams.get('ticket');
  const refCode = queryParams.get('ref') || undefined;

  const [event, setEvent] = useState<EventData | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const purchaseTrackedRef = useRef(false);

  const {
    showBanner: showMetaBanner,
    effectivePixel,
    accept: acceptMetaConsent,
    reject: rejectMetaConsent,
    manageCookies: manageMetaCookies,
    trackPurchase: trackMetaPurchase,
    trackAddToCart: trackMetaAddToCart,
    trackAddPaymentInfo: trackMetaAddPaymentInfo,
  } = useEventMetaPixel({
    eventSlug,
    eventId: event?._id,
    eventTitle: event?.title,
    metaPixel: event?.metaPixel,
    trackCheckoutOnMount: Boolean(event),
    disabled: event?.status === 'test',
  });
  const addToCartTrackedRef = useRef(false);
  const paymentInfoTrackedRef = useRef(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [validationContext, setValidationContext] = useState({ 
    isPaidTicket: false, 
    isFinalStep: false,
    allowInstallments: false
  });
  const creditCardFormRef = useRef<{ generateCardToken: () => Promise<void> }>(null);
  const [pixQRCodeBase64, setPixQRCodeBase64] = useState<string | null>(null);
  const [pixCode, setPixCode] = useState<string | null>(null);
  const [pixPaymentPending, setPixPaymentPending] = useState(false);
  const [registrationId, setRegistrationId] = useState<string | null>(null);
  const [checkInData, setCheckInData] = useState<{qrCodeBase64?: string; checkInCode?: string;} | null>(null);
  const [cardPaymentPending, setCardPaymentPending] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalModalType, setLegalModalType] = useState<'terms' | 'privacy' | 'refund'>('terms');
  const [legalAccepted, setLegalAccepted] = useState<LegalAcceptedItem[]>([]);
  const [platformInstallmentSettings, setPlatformInstallmentSettings] = useState({
    enabled: true,
    maxInstallments: 12,
  });
  const [checkoutPaymentTab, setCheckoutPaymentTab] = useState<CheckoutTab>('pix');
  const [paymentProviderRoutes, setPaymentProviderRoutes] = useState<PaymentProviderRoutesDto>({
    pix: 'mercadopago',
    credit_card: 'mercadopago',
    debit_card: 'mercadopago',
  });
  const [paymentRoutesReady, setPaymentRoutesReady] = useState(false);

  useEffect(() => {
    legalService
      .listTemplates()
      .then((templates) => setLegalAccepted(purchaseAcceptedFromTemplates(templates)))
      .catch(() => undefined);
  }, []);

  // Steps definition
  const steps = ['Seleção de Ingresso', 'Informações Pessoais', 'Confirmação e Pagamento'];

  const methods = useForm<RegistrationFormData>({
    resolver: yupResolver(createValidationSchema([])) as Resolver<RegistrationFormData, typeof validationContext>,
    context: validationContext, 
    defaultValues: {
      name: '',
      email: '',
      emailConfirmation: '',
      phone: '',
      ticketId: preSelectedTicketId || '',
      quantity: 1,
      customFields: {},
      agreeTerms: false,
      cpf: '',
      cardHolderName: '',
      cardNumber: '',
      cardExpirationDate: '',
      cardExpirationMonth: '',
      cardExpirationYear: '',
      cardCvv: '',
      installments: '1',
      cardHolderCPF: '',
      issuerId: '',
    },
    mode: 'onChange',
  });

  const { control, handleSubmit, watch, formState: { errors }, setValue, trigger, getValues } = methods;

  const selectedTicketId = watch('ticketId');
  const quantity = watch('quantity');

  const getSelectedTicket = useCallback(() => {
    if (!event || !event.tickets || !selectedTicketId) return null;
    return event.tickets.find((ticket) => String(ticket._id) === String(selectedTicketId)) ?? null;
  }, [event, selectedTicketId]);

  useEffect(() => {
    const selected = getSelectedTicket();
    if (!selected) return;
    const { min: allowedMin, max: allowedMax } = getRegistrationQuantityLimits(
      selected,
      event?.allowGroupRegistration
    );
    const current = Number(quantity) || 1;
    if (allowedMax > 0 && (current < allowedMin || current > allowedMax)) {
      setValue('quantity', allowedMin, { shouldValidate: true });
    }
  }, [event?.allowGroupRegistration, getSelectedTicket, quantity, selectedTicketId, setValue]);

  const catalogTickets = useMemo(
    () =>
      (event?.tickets || []).map((ticket) => ({
        _id: String(ticket._id),
        name: ticket.name,
        ticketTypeId: ticket.ticketTypeId,
      })),
    [event?.tickets]
  );

  const applicableCustomFields = useMemo(() => {
    if (!event?.customFields || !selectedTicketId) return [];
    const selectedTicket = getSelectedTicket();
    return event.customFields.filter((field) =>
      customFieldAppliesToTicket(field, String(selectedTicketId), selectedTicket, catalogTickets)
    );
  }, [catalogTickets, event?.customFields, getSelectedTicket, selectedTicketId]);

  const getApplicableCustomFields = useCallback(
    () => applicableCustomFields,
    [applicableCustomFields]
  );

  useEffect(() => {
    if (!event?.customFields || !selectedTicketId) return;
    const selectedTicket = getSelectedTicket();
    const current = getValues('customFields') || {};
    const next: Record<string, string | boolean | string[]> = { ...current };

    event.customFields.forEach((field) => {
      if (!customFieldAppliesToTicket(field, String(selectedTicketId), selectedTicket, catalogTickets)) {
        delete next[field._id];
      }
    });

    setValue('customFields', next);
  }, [catalogTickets, event?.customFields, getSelectedTicket, getValues, selectedTicketId, setValue]);

  const isSelectedTicketPaid = useCallback(() => {
    const ticket = getSelectedTicket();
    return ticket ? ticket.price > 0 : false;
  }, [getSelectedTicket]);

  /**
   * Calcula totais considerando a taxa da plataforma quando showPlatformFee estiver ativo.
   * - baseTotal: valor sem taxa (preço do ingresso * quantidade)
   * - feeAmount: valor da taxa (se aplicável)
   * - total: valor final que o participante deve pagar
   */
  const calculateTotals = useCallback(() => {
    const ticket = getSelectedTicket();
    const q = quantity || 1;
    if (!ticket) {
      return {
        baseTotal: 0,
        feeAmount: 0,
        total: 0,
        showFee: false,
        feePercentage: event?.feePercentage || 6.9,
      };
    }

    return calculateTicketTotalsWithFee({
      unitPrice: ticket.price,
      quantity: q,
      feePercentage: event?.feePercentage,
      showPlatformFee: ticket.showPlatformFee,
    });
  }, [getSelectedTicket, quantity, event?.feePercentage]);

  useEffect(() => {
    if (!success || !registrationId || purchaseTrackedRef.current) return;
    purchaseTrackedRef.current = true;
    const { total } = calculateTotals();
    const selected = getSelectedTicket();
    trackMetaPurchase(total, {
      numItems: Number(getValues('quantity')) || 1,
      contentIds: selected ? [String(selected._id)] : undefined,
      eventId: registrationId,
    });
  }, [success, registrationId, calculateTotals, trackMetaPurchase, getSelectedTicket, getValues]);

  useEffect(() => {
    // Re-validate form when custom fields are loaded
    if (event?.customFields) {
      trigger('customFields');
    }
  }, [event?.customFields, trigger]);

  useEffect(() => {
    const loadInstallmentSettings = async () => {
      try {
        const settings = await paymentServiceMercadoPago.getInstallmentSettings();
        setPlatformInstallmentSettings({
          enabled: settings.enabled !== false,
          maxInstallments: Math.max(1, Math.min(Number(settings.maxInstallments) || 12, 12)),
        });
      } catch (err) {
        console.warn('Falha ao carregar configurações globais de parcelamento. Usando padrão.', err);
      }
    };

    loadInstallmentSettings();
  }, []);

  useEffect(() => {
    if (!event?._id) {
      setPaymentRoutesReady(false);
      return;
    }
    let cancelled = false;
    const loadRoutes = async () => {
      setPaymentRoutesReady(false);
      try {
        const routes = await paymentServiceMercadoPago.getPaymentProviderRoutes({
          eventId: String(event._id),
        });
        if (!cancelled) {
          setPaymentProviderRoutes(routes);
        }
      } catch {
        if (!cancelled) {
          setPaymentProviderRoutes({
            pix: 'mercadopago',
            credit_card: 'mercadopago',
            debit_card: 'mercadopago',
          });
        }
      } finally {
        if (!cancelled) setPaymentRoutesReady(true);
      }
    };
    void loadRoutes();
    return () => {
      cancelled = true;
    };
  }, [event?._id]);

  useEffect(() => {
    const fetchEvent = async () => {
       try {
        setLoading(true);
        setError(null);
        if (!eventSlug) {
          setError('ID do evento não fornecido');
          setLoading(false);
          return;
        }
        const response = await eventService.getEventBySlug(eventSlug, preSelectedTicketId || undefined);
        console.log('🔍 [EventRegistrationPage] Response from API:', response);
        console.log('🔍 [EventRegistrationPage] CustomFields received:', response.data?.customFields);
        if (response.success && response.data) {
          const normalizedCustomFields = (response.data.customFields || []).map(
            (field: CustomFieldData) => normalizeCustomFieldScope(field) as CustomFieldData
          );
          setEvent({
            ...response.data,
            customFields: normalizedCustomFields,
          });
          if (!preSelectedTicketId && response.data.tickets && response.data.tickets.length > 0) {
            setValue('ticketId', response.data.tickets[0]._id, { shouldValidate: true });
          }
          
          // Inicializar valores padrão para campos customizados
          if (response.data.customFields) {
            const initialCustomFields: Record<string, string | boolean | string[]> = {};
            normalizedCustomFields.forEach((field: CustomFieldData) => {
              // Usar _id como chave única para evitar conflitos com campos de mesmo nome
              if (field.fieldType === 'checkbox') {
                initialCustomFields[field._id] = [];
              } else {
                initialCustomFields[field._id] = '';
              }
            });
            setValue('customFields', initialCustomFields);
            console.log('🔧 Inicializando customFields com valores padrão:', initialCustomFields);
            
            // Trigger validation after setting custom fields
            setTimeout(() => {
              trigger('customFields');
            }, 100);
          }
        } else {
          setError('Não foi possível carregar os detalhes do evento');
        }
      } catch (err) {
        console.error('Erro ao buscar evento:', err);
        const exclusiveDomain = (err as { response?: { data?: { exclusiveDomain?: string } } })?.response
          ?.data?.exclusiveDomain;
        if (exclusiveDomain) {
          replaceWithExclusiveDomain(exclusiveDomain);
          return;
        }
        setError('Erro ao carregar evento. Por favor, tente novamente.');
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventSlug, preSelectedTicketId, setValue, trigger]);

  useEffect(() => {
  if (!cardPaymentPending || !registrationId) return;

  let isActive = true;
  let attempt = 0;
  let delay = 5000;
  const maxAttempts = 6;

  const pollStatus = async () => {
    if (!isActive || attempt >= maxAttempts) return;

    try {
      console.log(`[Polling cartão] Tentativa ${attempt + 1} - aguardando ${delay / 1000}s...`);
      const result = await registrationService.checkStatus(registrationId);

      if (result.success && result.data.paymentStatus === 'paid') {
        console.log('[Polling cartão] Pagamento confirmado!');
        setSuccess(true);
        setCardPaymentPending(false);

        setCheckInData({
          qrCodeBase64: result.data.qrCodeBase64,
          checkInCode: result.data.checkInCode,
        });
        return;
      }

      attempt += 1;
      delay = Math.min(delay * 2, 160000);

      if (isActive) {
        setTimeout(pollStatus, delay);
      }
    } catch (err) {
      console.error('[Polling cartão] Erro ao verificar status:', err);
      attempt += 1;
      delay = Math.min(delay * 2, 160000);
      if (isActive) {
        setTimeout(pollStatus, delay);
      }
    }
  };

  pollStatus();

  return () => {
    console.log('[Polling cartão] Interrompido');
    isActive = false;
  };
}, [cardPaymentPending, registrationId]);

  useEffect(() => {
    if (!pixPaymentPending || !registrationId) return;

    let isActive = true;
    let attempt = 0;
    let delay = 5000; // começa com 5 segundos
    const maxAttempts = 6; // total de tentativas antes de parar (ex: 5s → 10s → 20s → 40s → 80s → 160s)

    const pollStatus = async () => {
      if (!isActive || attempt >= maxAttempts) return;

      try {
        console.log(`[Polling] Tentativa ${attempt + 1} - aguardando ${delay / 1000}s...`);
        const result = await registrationService.checkStatus(registrationId);

        if (result.success && result.data.paymentStatus === 'paid') {
          console.log('[Polling] Pagamento confirmado!');
          setSuccess(true);
          setPixPaymentPending(false);

          setCheckInData({
          qrCodeBase64: result.data.qrCodeBase64,
          checkInCode: result.data.checkInCode,
        });
          return; // interrompe polling
        }

        attempt += 1;
        delay = Math.min(delay * 2, 160000); // dobra até 160s máx.

        if (isActive) {
          setTimeout(pollStatus, delay);
        }
      } catch (err) {
        console.error('[Polling] Erro ao verificar status:', err);
        attempt += 1;
        delay = Math.min(delay * 2, 160000); // dobra delay mesmo em erro
        if (isActive) {
          setTimeout(pollStatus, delay);
        }
      }
    };

    pollStatus();

    return () => {
      console.log('[Polling] Interrompido');
      isActive = false;
    };
  }, [pixPaymentPending, registrationId]);

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return format(date, "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return dateString;
    }
  };

  const getFieldDisplayName = (fieldName: string): string => {
    const fieldNames: { [key: string]: string } = {
      'name': 'Nome',
      'email': 'Email',
      'phone': 'Telefone',
      'cpf': 'CPF',
      'agreeTerms': 'Aceite dos Termos',
      'ticketId': 'Tipo de Ingresso',
      'quantity': 'Quantidade'
    };
    return fieldNames[fieldName] || fieldName;
  };

  // Função para mapear customFields do formato do formulário (_id como chave) para o formato do backend (fieldName como chave)
  const mapCustomFieldsForBackend = (formCustomFields: Record<string, any>, customFields: CustomFieldData[]): Record<string, any> => {
    const mapped: Record<string, any> = {};
    const selectedTicket = getSelectedTicket();
    const catalogTickets = (event?.tickets || []).map((ticket) => ({
      _id: String(ticket._id),
      name: ticket.name,
      ticketTypeId: ticket.ticketTypeId,
    }));
    customFields.forEach(field => {
      if (!customFieldAppliesToTicket(field, String(selectedTicketId), selectedTicket, catalogTickets)) return;
      const fieldValue = formCustomFields[field._id];
      if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
        // Para arrays (checkbox), só incluir se não estiver vazio
        if (Array.isArray(fieldValue)) {
          if (fieldValue.length > 0) {
            mapped[field.fieldName] = fieldValue;
          }
        } else {
          mapped[field.fieldName] = fieldValue;
        }
      }
    });
    return mapped;
  };

  const handleNext = async () => {
    setValidationErrors([]); // Limpar erros anteriores
    let fieldsToValidate: (keyof RegistrationFormData)[] = [];
    let errorMessages: string[] = [];

    if (activeStep === 0) {
      fieldsToValidate = ['ticketId', 'quantity'];
      
      // Validar se o ticket selecionado está expirado
      const selectedTicket = getSelectedTicket();
      if (selectedTicket) {
        const isExpired = selectedTicket.endSaleDate && new Date(selectedTicket.endSaleDate) < new Date();
        const isOutOfStock = selectedTicket.quantity <= 0;
        
        if (isExpired) {
          setError('⚠️ As vendas para este ingresso foram encerradas. Por favor, selecione outro ingresso ou entre em contato com o organizador.');
          return;
        }
        
        if (isOutOfStock) {
          setError('⚠️ Este ingresso está esgotado. Por favor, selecione outro ingresso disponível.');
          return;
        }
      }
    } else if (activeStep === 1) {
      fieldsToValidate = ['name', 'email', 'emailConfirmation', 'phone', 'agreeTerms'];
      
      // Validar se o email está na lista permitida (se o ingresso for restrito)
      const selectedTicket = getSelectedTicket();
      const formValues = getValues();
      if (selectedTicket?.isRestricted && selectedTicket.allowedEmails && selectedTicket.allowedEmails.length > 0) {
        const userEmail = formValues.email?.toLowerCase().trim();
        const isEmailAllowed = selectedTicket.allowedEmails.some(
          (allowedEmail: string) => allowedEmail.toLowerCase().trim() === userEmail
        );
        
        if (!isEmailAllowed) {
          setError('⚠️ Seu email não está na lista de convidados permitidos para este ingresso. Entre em contato com o organizador do evento para adicionar seu email à lista.');
          return;
        }
      }

      if (event?.requiresBuyerCpf && isSelectedTicketPaid()) {
        const cpf = String(formValues.cpf || '');
        if (!validateCPF(cpf)) {
          errorMessages.push('❌ CPF de quem paga é obrigatório para a nota fiscal do ingresso');
          setValidationErrors(errorMessages);
          return;
        }
      }
      
      // Custom validation for custom fields
      if (event?.customFields) {
        const customFieldsErrors: string[] = [];
        const applicableFields = getApplicableCustomFields();
        
        console.log("🔍 Validating custom fields:", applicableFields);
        console.log("🔍 Current custom fields values:", formValues.customFields);
        
        applicableFields.forEach(cf => {
          if (cf.isRequired) {
            // Usar _id como chave única
            let fieldValue = formValues.customFields?.[cf._id];
            
            if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0) || fieldValue === '') {
              customFieldsErrors.push(cf._id);
              errorMessages.push(`❌ ${cf.fieldName} - Campo obrigatório não preenchido`);
            }
          }
        });
        
        if (customFieldsErrors.length > 0) {
          setValidationErrors(errorMessages);
          return;
        }
      }
    }

    const isValidStep = await trigger(fieldsToValidate);

    if (!isValidStep) {
      // Coletar erros de validação
      const formErrors = Object.keys(errors).filter(key => fieldsToValidate.includes(key as any));
      formErrors.forEach(field => {
        const fieldName = getFieldDisplayName(field);
        const errorMessage = (errors as any)[field]?.message || 'Campo obrigatório';
        errorMessages.push(`❌ ${fieldName}: ${errorMessage}`);
      });
      
      setValidationErrors(errorMessages);
      return;
    }

    // Se chegou até aqui, limpar erros e prosseguir
    setValidationErrors([]);

    if (activeStep === 0) {
      const selectedTicket = getSelectedTicket();
      const { total } = calculateTotals();
      if (!addToCartTrackedRef.current && selectedTicket) {
        addToCartTrackedRef.current = true;
        trackMetaAddToCart({
          value: total,
          numItems: Number(getValues('quantity')) || 1,
          contentIds: [String(selectedTicket._id)],
          contentName: selectedTicket.name,
        });
      }
    }

    if (activeStep === 1 && isSelectedTicketPaid() && !registrationId) {
      try {
        if (!legalAccepted.length) {
          setError('Não foi possível carregar os documentos legais. Recarregue a página.');
          return;
        }
        const formData = getValues();
        const { total } = calculateTotals(); 
        const response = await registrationService.createPending(event?._id!, {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          cpf: formData.cpf,
          ticketId: formData.ticketId,
          quantity: formData.quantity,
          customFields: mapCustomFieldsForBackend(formData.customFields, event?.customFields || []),
          // amountPaid deve refletir o valor total cobrado do participante (incluindo taxa se aplicável)
          amountPaid: total,
          ref: refCode,
          legalAccepted,
          meta: readMetaBrowserContext(),
        });

        if (response.success && (response.data?.id || (response.data as any)?._id)) {
          setRegistrationId(String(response.data.id || (response.data as any)._id));
        } else {
          setError(response.message || 'Erro ao salvar os dados antes do pagamento.');
          return;
        }
      } catch (err) {
        console.error('Erro ao salvar inscrição antes do pagamento:', err);
        setError(formatUnknownError(err, 'Erro ao salvar os dados antes do pagamento. Tente novamente.'));
        return;
      }
    }

    // Avança normalmente se tudo deu certo
    if (activeStep === 1 && isSelectedTicketPaid()) {
      const { total } = calculateTotals();
      const selectedTicket = getSelectedTicket();
      if (!paymentInfoTrackedRef.current) {
        paymentInfoTrackedRef.current = true;
        trackMetaAddPaymentInfo({
          value: total,
          contentIds: selectedTicket ? [String(selectedTicket._id)] : undefined,
          numItems: Number(getValues('quantity')) || 1,
        });
      }
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handlePagarmePixRequest = async (cpfDigits: string) => {
    if (!event || !selectedTicketId || !registrationId) {
      setPaymentError(t('Inscrição não encontrada. Conclua os passos anteriores e tente novamente.'));
      return;
    }
    const formData = getValues();
    const { total } = calculateTotals();

    setPaymentProcessing(true);
    setPaymentError(null);
    setError(null);

    try {
      const response = await paymentServiceMercadoPago.processPayment({
        ticketId: selectedTicketId,
        eventId: event._id,
        quantity: formData.quantity,
        customerEmail: formData.email,
        customerName: formData.name,
        customerPhone: formData.phone,
        customerCPF: cpfDigits,
        paymentMethodId: 'pix',
        installments: 1,
        transactionAmount: total,
        external_reference: registrationId,
        description: `Ingresso — ${event.title}`,
        customFields: mapCustomFieldsForBackend(formData.customFields, event?.customFields || []),
      });

      if (!response.success || !response.data) {
        setPaymentError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
        setError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
        return;
      }

      const paymentData = response.data;
      const transactionData = paymentData.point_of_interaction?.transaction_data;
      const qr = transactionData?.qr_code;
      if (String(paymentData?.status || '').toLowerCase() === 'failed') {
        setPaymentError(getPixFailureMessage(paymentData));
        return;
      }
      if (qr) {
        let b64 = transactionData?.qr_code_base64 as string | undefined;
        if (typeof b64 === 'string' && b64.startsWith('data:image')) {
          b64 = b64.split(',')[1];
        }
        setPixQRCodeBase64(b64 || null);
        setPixCode(qr);
        setPixPaymentPending(true);
        return;
      }

      setPaymentError(getPixFailureMessage(paymentData));
    } catch {
      setPaymentError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
      setError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
    } finally {
      setPaymentProcessing(false);
    }
  };

  // --- Mercado Pago Payment Handling ---
  const handleMercadoPagoPayment = async (
    cardTokenId: string, 
    paymentMethodId: string, 
    issuerId: string | null, 
    installmentsValue: number,
    payer?: {
      email?: string;
      first_name?: string;
      last_name?: string;
      identification?: {
        type: string;
        number: string;
      };
    },
    installmentTotalAmount?: number
  ) => {
    if (!event || !selectedTicketId) {
      setError('Erro: Evento ou Ingresso não selecionado.');
      setSubmitting(false);
      return;
    }
    const selectedTicket = getSelectedTicket();
    if (!selectedTicket) {
      setError('Erro: Detalhes do ingresso não encontrados.');
      setSubmitting(false);
      return;
    }

    const formData = getValues();
    const { total } = calculateTotals();
    const finalTransactionAmount = roundMoney(
      typeof installmentTotalAmount === 'number' && installmentTotalAmount > total
        ? installmentTotalAmount
        : total
    );

    setPaymentProcessing(true);
    setPaymentError(null);
    setError(null);

    try {
      const response = await paymentServiceMercadoPago.processPayment({
        ticketId: selectedTicketId,
        eventId: event._id,
        quantity: formData.quantity,
        customerEmail: formData.email,
        customerName: formData.cardHolderName || formData.name,
        customerCPF: payer?.identification?.number || '',
        customFields: mapCustomFieldsForBackend(formData.customFields, event?.customFields || []),
        cardToken: cardTokenId,
        paymentMethodId: paymentMethodId,
        issuerId: issuerId || undefined,
        installments: installmentsValue,
        // Mantém a regra atual de taxa; em parcelado, soma apenas o juros retornado pelo MP.
        transactionAmount: finalTransactionAmount,
        external_reference: registrationId ?? undefined,
      });


      console.log('🟢 Resposta do pagamento:', response);

      if (!response.success || !response.data) {
        setPaymentError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
        setError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
        return;
      }

      const paymentData = response.data;
      const methodFromResponse = paymentData.payment_method_id || paymentMethodId;

      // Pix (Mercado Pago Brick ou outro PSP normalizado): QR / copia-e-cola
      if (methodFromResponse === 'pix') {
        const transactionData = paymentData.point_of_interaction?.transaction_data;
        const qr = transactionData?.qr_code;
        if (String(paymentData?.status || '').toLowerCase() === 'failed') {
          setPaymentError(getPixFailureMessage(paymentData));
          return;
        }
        if (qr) {
          let b64 = transactionData?.qr_code_base64 as string | undefined;
          if (typeof b64 === 'string' && b64.startsWith('data:image')) {
            b64 = b64.split(',')[1];
          }
          setPixQRCodeBase64(b64 || null);
          setPixCode(qr);
          setPixPaymentPending(true);
          return;
        }
        setPaymentError(getPixFailureMessage(paymentData));
        return;
      }

      // Cartão: nunca mostrar tela de sucesso aqui — aguardar paymentStatus === 'paid' no backend (webhook + polling)
      const terminalFailureStatuses = [
        'rejected',
        'cancelled',
        'refunded',
        'charged_back',
        'failed',
        'canceled',
      ];
      if (terminalFailureStatuses.includes(paymentData.status)) {
        const detail =
          (paymentData as { status_detail?: string }).status_detail ||
          paymentData.status;
        setPaymentError(
          detail ? `Pagamento não concluído (${detail}). Verifique os dados do cartão ou tente outro cartão.` : 'Pagamento não concluído. Tente novamente.'
        );
        setError('Não foi possível concluir o pagamento.');
        return;
      }

      setCardPaymentPending(true);
    } catch {
      console.error('Erro inesperado ao processar pagamento');
      setPaymentError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
      setError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
    } finally {
      setPaymentProcessing(false);
      setSubmitting(false);
    }
  };

  /**
   * Função para baixar o PDF do ingresso
   */
  const handleDownloadTicket = async () => {
    if (!registrationId) {
      alert(t('ID da inscrição não encontrado. Por favor, tente novamente.'));
      return;
    }

    try {
      await registrationService.downloadTicketPDF(
        registrationId,
        `ingresso_${event?.title?.replace(/[^a-zA-Z0-9]/g, '_') || 'evento'}.pdf`
      );
    } catch (error) {
      console.error('Erro ao baixar ingresso:', error);
      alert('Erro ao baixar ingresso. Por favor, tente novamente ou entre em contato com o suporte.');
    }
  };

  const onSubmit = async (data: RegistrationFormData) => {
    console.log('🚀 [onSubmit] Final step submission triggered. Data:', data);
    console.log('🚀 [onSubmit] Current step:', activeStep);
    console.log('🚀 [onSubmit] Form data:', data);
    setSubmitting(true);
    setError(null);
    setPaymentError(null);

    const needsPayment = isSelectedTicketPaid(); 

    if (needsPayment) {
      console.log('[onSubmit] Ticket is paid. Triggering card token generation...');
      if (creditCardFormRef.current) {
        try {
            await creditCardFormRef.current.generateCardToken();
        } catch (tokenError) {
            console.error("[onSubmit] Error during token generation trigger:", tokenError);
            setError("Falha ao iniciar o processo de pagamento. Verifique os dados do cartão.");
            setSubmitting(false);
        }
      } else {
          console.error("[onSubmit] CreditCardForm ref not available.");
          setError("Erro interno ao processar pagamento. [Ref Error]");
          setSubmitting(false);
      }
    } else {
      console.log('[onSubmit] Ticket is free. Processing free registration...');
      try {
        if (!event) {
          setError('Evento não carregado. Tente novamente mais tarde.');
          setSubmitting(false);
          return;
        }
        if (!legalAccepted.length) {
          setError('Não foi possível carregar os documentos legais. Recarregue a página.');
          setSubmitting(false);
          return;
        }

        const result = await registrationService.registerFree(event._id, {
          name: data.name,
          email: data.email,
          phone: data.phone,
          ticketId: data.ticketId,
          quantity: data.quantity,
          customFields: mapCustomFieldsForBackend(data.customFields, event?.customFields || []),
          ref: refCode,
          legalAccepted,
        });

        console.log('📋 Resultado da inscrição gratuita:', result);

        if (result.success && result.data) {
          // Extrair o ID (pode vir como _id do MongoDB ou id)
          const regId = (result.data as any)._id || result.data.id;
          console.log('✅ Inscrição criada com ID:', regId);
          setRegistrationId(regId);
          
          // Buscar dados completos com QR Code
          try {
            console.log('🔍 Buscando status da inscrição...');
            const statusResult = await registrationService.checkStatus(regId);
            console.log('📊 Status recebido:', statusResult);
            
            if (statusResult.success && statusResult.data) {
              if (statusResult.data.qrCodeBase64) {
                console.log('✅ QR Code encontrado!');
                setCheckInData({
                  qrCodeBase64: statusResult.data.qrCodeBase64,
                  checkInCode: statusResult.data.checkInCode,
                });
              } else {
                console.warn('⚠️ QR Code não disponível ainda');
              }
            }
          } catch (statusErr) {
            console.error('❌ Erro ao buscar status:', statusErr);
          }
          
          setSuccess(true);
        } else {
          throw new Error(result.message || t('Erro ao registrar inscrição gratuita.'));
        }
      } catch (freeRegError: any) {
        console.error('❌ Erro na inscrição gratuita:', freeRegError);
        setError(formatUnknownError(freeRegError, t('Erro ao completar a inscrição gratuita.')));
        setSubmitting(false);
      } finally {
        setSubmitting(false);
      }
    }
  };

  useEffect(() => {
    const needsPayment = isSelectedTicketPaid();
    const finalStep = activeStep === steps.length - 1;
    const allowInstallments = needsPayment && platformInstallmentSettings.enabled;
    setValidationContext({ 
      isPaidTicket: needsPayment, 
      isFinalStep: finalStep,
      allowInstallments: allowInstallments 
    });
  }, [activeStep, isSelectedTicketPaid, steps.length, platformInstallmentSettings.enabled]);
  
  const getErrorMessage = (error: unknown) => formatUnknownError(error, '');

  // --- Step Content Rendering ---
  const renderStepContent = (step: number) => {
    const needsPayment = isSelectedTicketPaid(); // Check payment need for rendering

    switch (step) {
      case 0: // Ticket Selection
        return (
          <Box>
            <Typography variant="h6">Selecione seu ingresso</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
                Ingresso para participação presencial. Nenhum produto digital será entregue.
              </Typography>
              <Box sx={{ mb: 2 }} />

              {event?.ticketCatalogMode === 'phased' && event.activeSalesPhase && (
                <ActiveLotBanner phase={event.activeSalesPhase} />
              )}

             {event && event.tickets && event.tickets.length > 0 ? (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.ticketId}>
                  <InputLabel id="ticket-select-label">Tipo de Ingresso</InputLabel>
                  <Controller
                    name="ticketId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="ticket-select-label"
                        label="Tipo de Ingresso"
                        value={field.value || ''}
                        onChange={(e) => {
                          field.onChange(e);
                        }}
                      >
                        {event.tickets.map((ticket) => {
                          const isExpired = ticket.endSaleDate && new Date(ticket.endSaleDate) < new Date();
                          const isOutOfStock = ticket.quantity <= 0;
                          const isUnavailable = isExpired || isOutOfStock;
                          const feePercentage = event?.feePercentage || 6.9;
                          const showFee = ticket.showPlatformFee && ticket.price > 0;
                          const ticketTotals = calculateTicketTotalsWithFee({
                            unitPrice: ticket.price,
                            quantity: 1,
                            feePercentage,
                            showPlatformFee: ticket.showPlatformFee,
                          });
                          const totalWithFee = ticketTotals.total;
                          
                          return (
                            <MenuItem 
                              key={ticket._id} 
                              value={String(ticket._id)}
                              disabled={isUnavailable}
                              sx={{
                                opacity: isUnavailable ? 0.5 : 1
                              }}
                            >
                              <Box sx={{ width: '100%' }}>
                                <Typography variant="body1">
                                  {ticket.name} - {ticket.price > 0 ? `R$ ${totalWithFee.toFixed(2)}` : 'Gratuito'}
                                </Typography>
                                {getTicketLotName(ticket) && (
                                  <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 600 }}>
                                    Lote: {getTicketLotName(ticket)}
                                  </Typography>
                                )}
                                {showFee && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    R$ {ticket.price.toFixed(2)} + taxa R$ {ticketTotals.feeAmount.toFixed(2)}
                                  </Typography>
                                )}
                                {ticket.isRestricted && (
                                  <Typography variant="caption" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                    <EmailIcon sx={{ fontSize: '0.875rem' }} /> Exclusivo para convidados
                                  </Typography>
                                )}
                                {isExpired && <Typography variant="caption" color="error">(Vendas Encerradas)</Typography>}
                                {isOutOfStock && !isExpired && <Typography variant="caption" color="error">(Esgotado)</Typography>}
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    )}
                  />
                  {errors.ticketId && (
                    <FormHelperText>{errors.ticketId.message as string}</FormHelperText>
                  )}
                </FormControl>
                </Grid>
                {event?.ticketCatalogMode === 'batched' && (() => {
                  const ticket = getSelectedTicket();
                  const lot = ticket ? getTicketLotName(ticket) : undefined;
                  if (!lot) return null;
                  return (
                    <Grid item xs={12}>
                      <ActiveLotBanner lotName={lot} endSaleDate={ticket?.endSaleDate} compact />
                    </Grid>
                  );
                })()}
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.quantity}>
                    <InputLabel id="quantity-select-label">Quantidade</InputLabel>
                    <Controller
                      name="quantity"
                      control={control}
                      render={({ field }) => {
                        const selected = getSelectedTicket();
                        const limits = selected
                          ? getRegistrationQuantityLimits(selected, event?.allowGroupRegistration)
                          : { min: 1, max: 1 };
                        const maxOpt = Math.max(limits.min, limits.max);
                        const minOpt = limits.min;
                        const options: number[] = [];
                        for (let n = minOpt; n <= maxOpt; n += 1) options.push(n);
                        if (options.length === 0) options.push(1);
                        const value = options.includes(Number(field.value))
                          ? Number(field.value)
                          : options[0];
                        return (
                          <Select
                            {...field}
                            value={value}
                            labelId="quantity-select-label"
                            label="Quantidade"
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          >
                            {options.map((num) => (
                              <MenuItem key={num} value={num}>
                                {num}
                              </MenuItem>
                            ))}
                          </Select>
                        );
                      }}
                    />
                    {errors.quantity && (
                      <FormHelperText>{errors.quantity.message as string}</FormHelperText>
                    )}
                    {(() => {
                      const selected = getSelectedTicket();
                      const limits = selected
                        ? getRegistrationQuantityLimits(selected, event?.allowGroupRegistration)
                        : { min: 1, max: 1 };
                      if (limits.max > 1) {
                        return (
                          <FormHelperText>
                            {t(`Você pode comprar até ${limits.max} deste ingresso neste pedido.`)}
                          </FormHelperText>
                        );
                      }
                      return (
                        <FormHelperText>
                          {t(
                            'Inscrição individual apenas. Para inscrições em grupo, entre em contato com a organização.'
                          )}
                        </FormHelperText>
                      );
                    })()}
                  </FormControl>
                </Grid>
                
                {/* Aviso para ingressos restritos */}
                {(() => {
                  const selectedTicket = getSelectedTicket();
                  if (selectedTicket?.isRestricted && selectedTicket.allowedEmails && selectedTicket.allowedEmails.length > 0) {
                    return (
                      <Grid item xs={12}>
                        <Alert severity="warning" icon={<EmailIcon />} sx={{ mt: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                            ⚠️ Este ingresso é exclusivo para convidados
                          </Typography>
                          <Typography variant="caption">
                            Apenas pessoas com emails cadastrados na lista de convidados podem comprar este ingresso. 
                            Se seu email não estiver na lista, entre em contato com o organizador do evento antes de prosseguir.
                          </Typography>
                        </Alert>
                      </Grid>
                    );
                  }
                  return null;
                })()}
                
                {getSafeTicketDescription(getSelectedTicket()) && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {getSafeTicketDescription(getSelectedTicket())}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Typography>Nenhum ingresso disponível para este evento.</Typography>
            )}
          </Box>
        );
      case 1: // Personal Information
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>Suas Informações</Typography>
            
            {/* Alerta destacado sobre o email */}
            <Alert 
              severity="warning" 
              icon={<Box sx={{ fontSize: '1.5rem' }}>⚠️</Box>}
              sx={{ 
                mb: 3,
                '& .MuiAlert-message': {
                  fontSize: '0.95rem',
                  fontWeight: 500
                }
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Atenção: Seu ingresso será enviado para o email informado!
              </Typography>
              <Typography variant="body2">
                Confira cuidadosamente se o email está correto. Você precisará dele para acessar seu ingresso.
              </Typography>
            </Alert>

            <Grid container spacing={2}>
               <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Nome Completo"
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message as string}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <TextField
                        {...field}
                        label="Email *"
                        type="email"
                        fullWidth
                        autoComplete="off"
                        error={!!errors.email}
                        helperText={errors.email?.message as string || 'Digite seu email com atenção'}
                        onChange={(e) => {
                          field.onChange(e);
                          const suggestion = suggestEmailDomain(e.target.value);
                          setEmailSuggestion(suggestion);
                        }}
                        InputProps={{
                          endAdornment: watch('email') && !errors.email && watch('email').includes('@') ? (
                            <Box sx={{ color: 'success.main', fontSize: '1.2rem' }}>✓</Box>
                          ) : null
                        }}
                      />
                      {emailSuggestion && (
                        <Alert 
                          severity="info" 
                          sx={{ mt: 1 }}
                          action={
                            <Button 
                              color="inherit" 
                              size="small"
                              onClick={() => {
                                setValue('email', emailSuggestion);
                                setEmailSuggestion(null);
                                trigger('email');
                              }}
                            >
                              Corrigir
                            </Button>
                          }
                        >
                          Você quis dizer <strong>{emailSuggestion}</strong>?
                        </Alert>
                      )}
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="emailConfirmation"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Confirme seu Email *"
                      type="email"
                      fullWidth
                      autoComplete="off"
                      error={!!errors.emailConfirmation}
                      helperText={errors.emailConfirmation?.message as string || 'Digite o email novamente para confirmar'}
                      onPaste={(e) => e.preventDefault()}
                      InputProps={{
                        endAdornment: watch('emailConfirmation') && !errors.emailConfirmation && watch('email') === watch('emailConfirmation') ? (
                          <Box sx={{ color: 'success.main', fontSize: '1.2rem' }}>✓</Box>
                        ) : null
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Telefone (com DDD)"
                      placeholder="(11) 98765-4321"
                      fullWidth
                      error={!!errors.phone}
                      helperText={errors.phone?.message as string || "Ex: (11) 98765-4321 para celular ou (11) 3456-7890 para fixo"}
                      onChange={(e) => {
                        const formatted = applyPhoneMask(e.target.value);
                        field.onChange(formatted);
                      }}
                      inputProps={{
                        maxLength: 15, // (11) 98765-4321
                      }}
                    />
                  )}
                />
              </Grid>
              {event?.requiresBuyerCpf && isSelectedTicketPaid() && (
                <Grid item xs={12}>
                  <Controller
                    name="cpf"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="CPF de quem paga *"
                        placeholder="000.000.000-00"
                        fullWidth
                        helperText="Obrigatório para a nota fiscal do ingresso"
                        onChange={(e) => field.onChange(applyCpfMask(e.target.value))}
                        inputProps={{ inputMode: 'numeric', maxLength: 14 }}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Render Custom Fields */} 
              {getApplicableCustomFields()
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map((field) => (
                <Grid item xs={12} key={field._id}>
                  <FormControl fullWidth error={!!errors.customFields?.[field._id]}>
                    {field.fieldType === 'checkbox' ? (
                      <Box>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            mb: 3, 
                            fontWeight: 600,
                            fontSize: '1.1rem',
                            color: 'text.primary'
                          }}
                        >
                          {field.fieldName + (field.isRequired ? ' *' : '')}
                        </Typography>
                        <Controller
                          name={`customFields.${field._id}`}
                          control={control}
                          defaultValue={[]}
                          render={({ field: checkboxField }) => (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {field.options?.map((option, optionIndex) => (
                                <Paper
                                  key={`${field._id}-${optionIndex}-${option}`}
                                  elevation={1}
                                  sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    border: Array.isArray(checkboxField.value) && checkboxField.value.includes(option) 
                                      ? '2px solid #1976d2' 
                                      : '2px solid #e0e0e0',
                                    backgroundColor: Array.isArray(checkboxField.value) && checkboxField.value.includes(option) 
                                      ? '#f3f8ff' 
                                      : '#ffffff',
                                    transition: 'all 0.2s ease-in-out',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      borderColor: '#1976d2',
                                      backgroundColor: '#f9f9f9'
                                    }
                                  }}
                                  onClick={() => {
                                    const currentValues = Array.isArray(checkboxField.value) ? checkboxField.value : [];
                                    if (currentValues.includes(option)) {
                                      checkboxField.onChange(currentValues.filter((val: string) => val !== option));
                                    } else {
                                      checkboxField.onChange([...currentValues, option]);
                                    }
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Checkbox
                                      checked={Array.isArray(checkboxField.value) ? checkboxField.value.includes(option) : false}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        const currentValues = Array.isArray(checkboxField.value) ? checkboxField.value : [];
                                        if (e.target.checked) {
                                          checkboxField.onChange([...currentValues, option]);
                                        } else {
                                          checkboxField.onChange(currentValues.filter((val: string) => val !== option));
                                        }
                                      }}
                                      sx={{
                                        '&.Mui-checked': {
                                          color: '#1976d2',
                                        },
                                        transform: 'scale(1.2)'
                                      }}
                                    />
                                    <Typography 
                                      variant="body1" 
                                      sx={{ 
                                        fontSize: '1rem',
                                        fontWeight: Array.isArray(checkboxField.value) && checkboxField.value.includes(option) ? 500 : 400,
                                        color: 'text.primary',
                                        flex: 1
                                      }}
                                    >
                                      {option}
                                    </Typography>
                                  </Box>
                                </Paper>
                              ))}
                            </Box>
                          )}
                        />
                      </Box>
                    ) : field.fieldType === 'select' ? (
                      <>
                        <InputLabel id={`custom-${field._id}-label`}>{field.fieldName + (field.isRequired ? ' *' : '')}</InputLabel>
                        <Controller
                          name={`customFields.${field._id}`}
                          control={control}
                          defaultValue=""
                          render={({ field: selectField }) => (
                            <Select
                              {...selectField}
                              labelId={`custom-${field._id}-label`}
                              label={field.fieldName + (field.isRequired ? ' *' : '')}
                            >
                              {field.options?.map((option) => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                      </>
                    ) : field.fieldType === 'label' ? (
                      <Typography
                        variant="body1"
                        sx={{
                          cursor: 'pointer',
                          color: 'primary.main',
                          textDecoration: 'underline',
                          fontWeight: 500,
                          py: 1,
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            color: 'primary.dark',
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {field.fieldName}
                      </Typography>
                    ) : field.fieldType === 'cpf' ? (
                      <Controller
                        name={`customFields.${field._id}`}
                        control={control}
                        defaultValue=""
                        render={({ field: cpfField }) => (
                          <TextField
                            {...cpfField}
                            label={field.fieldName + (field.isRequired ? ' *' : '')}
                            placeholder="000.000.000-00"
                            fullWidth
                            error={!!errors.customFields?.[field._id]}
                            helperText={errors.customFields?.[field._id]?.message as string || 'Digite apenas números'}
                            onChange={(e) => {
                              const formatted = applyCpfMask(e.target.value);
                              cpfField.onChange(formatted);
                            }}
                            inputProps={{ maxLength: 14 }}
                          />
                        )}
                      />
                    ) : field.fieldType === 'cnpj' ? (
                      <Controller
                        name={`customFields.${field._id}`}
                        control={control}
                        defaultValue=""
                        render={({ field: cnpjField }) => (
                          <TextField
                            {...cnpjField}
                            label={field.fieldName + (field.isRequired ? ' *' : '')}
                            placeholder="00.000.000/0000-00"
                            fullWidth
                            error={!!errors.customFields?.[field._id]}
                            helperText={errors.customFields?.[field._id]?.message as string || 'Digite apenas números'}
                            onChange={(e) => {
                              const formatted = applyCnpjMask(e.target.value);
                              cnpjField.onChange(formatted);
                            }}
                            inputProps={{ maxLength: 18 }}
                          />
                        )}
                      />
                    ) : field.fieldType === 'cep' ? (
                      <Controller
                        name={`customFields.${field._id}`}
                        control={control}
                        defaultValue=""
                        render={({ field: cepField }) => (
                          <TextField
                            {...cepField}
                            label={field.fieldName + (field.isRequired ? ' *' : '')}
                            placeholder="00000-000"
                            fullWidth
                            error={!!errors.customFields?.[field._id]}
                            helperText={errors.customFields?.[field._id]?.message as string || 'Digite apenas números'}
                            onChange={(e) => {
                              const formatted = applyCepMask(e.target.value);
                              cepField.onChange(formatted);
                            }}
                            inputProps={{ maxLength: 9 }}
                          />
                        )}
                      />
                    ) : field.fieldType === 'idade' ? (
                      <Controller
                        name={`customFields.${field._id}`}
                        control={control}
                        defaultValue=""
                        render={({ field: idadeField }) => (
                          <TextField
                            {...idadeField}
                            label={field.fieldName + (field.isRequired ? ' *' : '')}
                            placeholder="Ex: 25"
                            fullWidth
                            error={!!errors.customFields?.[field._id]}
                            helperText={errors.customFields?.[field._id]?.message as string || 'Digite a idade (0 a 150)'}
                            onChange={(e) => {
                              const formatted = applyIdadeMask(e.target.value);
                              idadeField.onChange(formatted);
                            }}
                            inputProps={{ maxLength: 3 }}
                          />
                        )}
                      />
                    ) : field.fieldType === 'dataNascimento' ? (
                      <Controller
                        name={`customFields.${field._id}`}
                        control={control}
                        defaultValue=""
                        render={({ field: dataField }) => (
                          <TextField
                            {...dataField}
                            label={field.fieldName + (field.isRequired ? ' *' : '')}
                            placeholder="DD/MM/AAAA"
                            fullWidth
                            error={!!errors.customFields?.[field._id]}
                            helperText={errors.customFields?.[field._id]?.message as string || 'Digite a data no formato DD/MM/AAAA'}
                            onChange={(e) => {
                              const formatted = applyDataNascimentoMask(e.target.value);
                              dataField.onChange(formatted);
                            }}
                            inputProps={{ maxLength: 10 }}
                          />
                        )}
                      />
                    ) : ( // text or number
                      <Controller
                        name={`customFields.${field._id}`}
                        control={control}
                        defaultValue=""
                        render={({ field: textField }) => (
                          <TextField
                            {...textField}
                            label={field.fieldName + (field.isRequired ? ' *' : '')}
                            type={field.fieldType === 'number' ? 'number' : 'text'}
                            fullWidth
                            error={!!errors.customFields?.[field._id]}
                            helperText={errors.customFields?.[field._id]?.message as string}
                          />
                        )}
                      />
                    )}
                    {errors.customFields && errors.customFields[field._id] && (
                      <FormHelperText>{errors.customFields[field._id]?.message as string}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              ))}

              {/* Agree Terms Checkbox - Simplificado */} 
              <Grid item xs={12}>
                <FormControl error={!!errors.agreeTerms} required fullWidth>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Controller
                      name="agreeTerms"
                      control={control}
                      render={({ field }) => (
                        <Checkbox 
                          {...field} 
                          checked={!!field.value}
                          sx={{
                            mt: -0.5,
                            '& .MuiSvgIcon-root': {
                              fontSize: 24
                            }
                          }}
                        />
                      )}
                    />
                    <Box sx={{ flex: 1, pt: 0.5 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          lineHeight: 1.6,
                          color: 'text.primary',
                          fontSize: '0.875rem'
                        }}
                      >
                        Li e concordo com os{' '}
                        <Box
                          component="span"
                          onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setLegalModalType('terms');
                            setLegalModalOpen(true);
                          }}
                          sx={{ 
                            color: 'primary.main',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            display: 'inline',
                            '&:hover': {
                              color: 'primary.dark'
                            }
                          }}
                        >
                          Termos de Uso
                        </Box>
                        ,{' '}
                        <Box
                          component="span"
                          onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setLegalModalType('privacy');
                            setLegalModalOpen(true);
                          }}
                          sx={{ 
                            color: 'primary.main',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            display: 'inline',
                            '&:hover': {
                              color: 'primary.dark'
                            }
                          }}
                        >
                          Política de Privacidade
                        </Box>
                        {' '}e{' '}
                        <Box
                          component="span"
                          onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setLegalModalType('refund');
                            setLegalModalOpen(true);
                          }}
                          sx={{ 
                            color: 'primary.main',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            display: 'inline',
                            '&:hover': {
                              color: 'primary.dark'
                            }
                          }}
                        >
                          Política de Reembolso
                        </Box>
                      </Typography>
                    </Box>
                  </Box>
                  {errors.agreeTerms && (
                    <FormHelperText 
                      sx={{ 
                        mt: 0.5, 
                        ml: 0,
                        fontSize: '0.875rem'
                      }}
                    >
                      {errors.agreeTerms.message as string}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );
      case 2: {
        const ticket = getSelectedTicket();
        const { baseTotal, feeAmount, total, showFee, feePercentage } = calculateTotals();
        
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>{t('Confirme sua Inscrição')}</Typography>
            <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Resumo do Pedido</Typography>
              <Typography>Evento: {event?.title}</Typography>
              <Typography>Ingresso: {ticket?.name}</Typography>
              <Typography>Quantidade: {quantity}</Typography>
              
              {showFee ? (
                <>
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Typography>Valor do ingresso:</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {quantity}x R$ {ticket?.price.toFixed(2).replace('.', ',')} = R$ {baseTotal.toFixed(2).replace('.', ',')}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography>Taxa da plataforma ({feePercentage.toFixed(1).replace('.', ',')}%):</Typography>
                    <Typography variant="body2" color="text.secondary">
                      R$ {feeAmount.toFixed(2).replace('.', ',')}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Total a pagar:</Typography>
                    <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                      R$ {total.toFixed(2).replace('.', ',')}
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Typography>Valor Unitário: {ticket?.price ? `R$ ${ticket.price.toFixed(2)}` : 'Gratuito'}</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="h6">Total: R$ {baseTotal.toFixed(2)}</Typography>
                </>
              )}
            </Paper>

            {needsPayment ? (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Informações de Pagamento
                </Typography>

                {/* Pagamento via Pix - se gerado */}
                {(cardPaymentPending || pixPaymentPending) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Seu pagamento está em processamento. Assim que for confirmado, você receberá o ingresso por e-mail e ele ficará disponível aqui. Isso pode levar alguns minutos dependendo do método de pagamento.
                </Alert>
              )}
                {pixPaymentPending && pixCode ? (
                  <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Typography variant="h6">Pagamento via Pix</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Escaneie o QR Code abaixo com seu app bancário ou copie o código Pix.
                    </Typography>
                    <Box sx={{ my: 2 }}>
                      {pixQRCodeBase64 ? (
                        <img
                          src={`data:image/png;base64,${pixQRCodeBase64}`}
                          alt="QR Code Pix"
                          style={{ width: 'min(256px, 100%)', height: 'auto', aspectRatio: '1 / 1' }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Use o código copia e cola abaixo no app do seu banco.
                        </Typography>
                      )}
                    </Box>
                    <PixCopyCodeField code={pixCode} />
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Após o pagamento, você receberá a confirmação por e-mail assim que for processado.
                    </Alert>
                  </Box>
                ) : (
                  // Cartão ou antes de escolher Pix
                  <Box>
                    {!paymentRoutesReady ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                        <CircularProgress size={28} />
                        <Typography variant="body2" color="text.secondary">
                          Carregando formas de pagamento…
                        </Typography>
                      </Box>
                    ) : (
                      <CheckoutPaymentRouter
                        amount={total}
                        payerEmail={watch('email')}
                        routes={paymentProviderRoutes}
                        checkoutTab={checkoutPaymentTab}
                        onCheckoutTabChange={setCheckoutPaymentTab}
                        onCardTokenReceived={handleMercadoPagoPayment}
                        onError={(errorMessage) => setPaymentError(toErrorMessage(errorMessage))}
                        allowInstallments={platformInstallmentSettings.enabled}
                        maxInstallments={platformInstallmentSettings.maxInstallments}
                        customerName={watch('name')}
                        customerCPF={(watch('cardHolderCPF') || '').replace(/\D/g, '')}
                        onRequestPagarmePix={handlePagarmePixRequest}
                        pagarmePixBusy={paymentProcessing}
                      />
                    )}

                    {paymentError && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {getErrorMessage(paymentError)}
                      </Alert>
                    )}
                    {(cardPaymentPending || pixPaymentPending) && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Seu pagamento está em processamento. Assim que for confirmado, você receberá o ingresso por e-mail e ele ficará disponível aqui. Isso pode levar alguns minutos dependendo do método de pagamento.
                    </Alert>
                  )}
                  </Box>
                )}
              </Box>
            ) : (
              <Typography sx={{ mt: 2 }}>
                {t('Seu ingresso é gratuito. Clique em "Confirmar Inscrição" para finalizar.')}
              </Typography>
            )}
          </Box>
        );
      }
      default:
        return 'Passo desconhecido';
    }
  };

  // --- Main Render ---
  const withMetaChrome = (node: React.ReactNode) => (
    <>
      {node}
      {effectivePixel && !showMetaBanner ? (
        <Box sx={{ position: 'fixed', right: 16, bottom: 16, zIndex: 1300 }}>
          <MetaConsentManageLink onManage={manageMetaCookies} />
        </Box>
      ) : null}
      {showMetaBanner ? (
        <MetaConsentBanner
          onAccept={acceptMetaConsent}
          onReject={rejectMetaConsent}
          brandKey={(event as { brandKey?: string } | null)?.brandKey}
        />
      ) : null}
    </>
  );

  if (loading) {
    return <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Container>;
  }

  if (error && !event) {
    return <Container><Alert severity="error">{error}</Alert></Container>;
  }

  if (!event) {
     return <Container><Alert severity="info">Evento não encontrado.</Alert></Container>;
  }

  if (
    event.allowGroupRegistration ||
    (event.tickets || []).some((ticket) => hasExplicitMultiPurchase(ticket))
  ) {
    return withMetaChrome(
      <EventCartCheckout
        event={event}
        eventSlug={eventSlug!}
        preSelectedTicketId={preSelectedTicketId}
        refCode={refCode}
      />
    );
  }

  // Cartão: não exibir tela verde de sucesso até o backend confirmar paymentStatus === 'paid' (polling abaixo)
  if (cardPaymentPending && !success && isSelectedTicketPaid() && registrationId) {
    return withMetaChrome(
      <Container maxWidth="sm" sx={{ my: 6 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
            border: (t) => `1px solid ${t.palette.divider}`,
            bgcolor: 'background.paper',
          }}
        >
          <CircularProgress size={52} thickness={4} sx={{ mb: 3 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Processando seu pagamento
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2, maxWidth: 420, mx: 'auto' }}>
            Seu pagamento com cartão foi enviado e está sendo analisado. Isso pode levar alguns instantes — não feche esta página por enquanto.
          </Typography>
          {event?.title ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Evento: <strong>{event.title}</strong>
            </Typography>
          ) : null}
          <Alert severity="info" sx={{ textAlign: 'left' }}>
            {t('A confirmação da inscrição só aparece aqui quando o pagamento estiver realmente aprovado. Você também receberá o e-mail com o ingresso assim que confirmarmos.')}
          </Alert>
        </Paper>
      </Container>
    );
  }

  if (success) {
    const needsPaymentOnSuccess = isSelectedTicketPaid();
    const formData = getValues();
    const selectedTicket = getSelectedTicket();
    const { total: totalAmount } = calculateTotals();

    return withMetaChrome(
      <RegistrationSuccessView
        event={event}
        attendees={[
          {
            registrationId: registrationId || '',
            participant: {
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
            },
            ticket: selectedTicket ?? null,
            checkInData: checkInData || undefined,
          },
        ]}
        totalAmount={totalAmount}
        needsPayment={needsPaymentOnSuccess}
        quantity={formData.quantity}
        onDownloadTicket={() => {
          if (registrationId) void handleDownloadTicket();
        }}
      />
    );
  }

  return withMetaChrome(
    <Container maxWidth="md" sx={{ my: 4 }}>
      <Paper elevation={2} sx={{ p: isMobile ? 2 : 4 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" gutterBottom align="center">
          {event.title}
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
          {formatDate(event.startDate)} - {formatDate(event.endDate)}
        </Typography>

        <Stepper activeStep={activeStep} alternativeLabel={isMobile} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit, (errors) => {
            console.error('❌ Erros de validação do formulário:', errors);
            console.log('📋 Valores atuais do formulário:', getValues());
          })} noValidate>
            {renderStepContent(activeStep)}

            {error && activeStep !== steps.length -1 && (
                 <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            )}

            {/* Exibir erros de validação */}
            {validationErrors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Por favor, corrija os seguintes campos:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {validationErrors.map((error, index) => (
                    <li key={index}>
                      <Typography variant="body2">{error}</Typography>
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, gap: 2, flexWrap: 'wrap' }}>
              <Button
                disabled={activeStep === 0 || submitting || paymentProcessing}
                onClick={handleBack}
              >
                Voltar
              </Button>
              {activeStep < steps.length - 1 ? (
                <Button variant="contained" onClick={handleNext}>
                  Próximo
                </Button>
              ) : isSelectedTicketPaid() ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: { xs: 'left', sm: 'right' }, maxWidth: 380 }}>
                  Finalize o pagamento com o botão <strong>Pagar</strong> no formulário acima (cartão ou Pix).
                </Typography>
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitting || paymentProcessing}
                >
                  {(submitting || paymentProcessing) ? <CircularProgress size={24} color="inherit" /> : t('Confirmar Inscrição')}
                </Button>
              )}
            </Box>
          </form>
        </FormProvider>
      </Paper>

      {/* Modal de Termos Legais */}
      <LegalModal 
        open={legalModalOpen}
        onClose={() => setLegalModalOpen(false)}
        type={legalModalType}
      />
    </Container>
  );
};

export default EventRegistrationPage;

